import { Head, useForm } from "@inertiajs/react";
import { t } from "@travily/i18n";
import { productEditorPath, productEditorSectionPath, productPath } from "@travily/routes";
import type ListingsEditorTitle from "@travily/types/pages/ListingsEditorTitle";
import { ButtonDock } from "@ui/components/ButtonDock";
import { StandardButton } from "@ui/components/StandardButton";
import type { ReactNode } from "react";
import { EditFrame } from "@/layouts/edit-frame";
import PersistentLayout from "@/layouts/persistent-layout";
import { buildEditorSections } from "@/lib/editor-sections";
import { firstError } from "@/lib/forms/first-error";
import { ProductTitleEditor } from "./show.view";

type PageProps = ListingsEditorTitle;

interface TitleFormData {
  title: string;
}

const ProductEditTitlePage = (props: PageProps) => {
  const { listing_id, title } = props;
  const sections = buildEditorSections(props);
  const form = useForm<TitleFormData>({ title: title ?? "" });

  const handleSave = () => {
    form.patch(productEditorSectionPath(listing_id, "title"), {
      preserveScroll: "errors",
    });
  };

  return (
    <EditFrame
      activeSection="title"
      actions={
        <ButtonDock
          primaryAction={
            <>
              <StandardButton
                className="sm:hidden"
                color="green"
                isDisabled={!form.isDirty || form.processing}
                onPress={handleSave}
                size="56"
              >
                {t("common.actions.save")}
              </StandardButton>
              <StandardButton
                className="hidden sm:flex"
                color="green"
                isDisabled={!form.isDirty || form.processing}
                onPress={handleSave}
                size="64"
              >
                {t("common.actions.save")}
              </StandardButton>
            </>
          }
        />
      }
      backHref={productPath(listing_id)}
      href={productEditorPath(listing_id)}
      sections={sections}
      title={t("web.products.editors.show.title")}
    >
      <Head title={t("web.products.editor.sections.show.meta_title", { title: props.listing_title ?? "" })} />
      <ProductTitleEditor
        error={firstError(form.errors.title as string | string[] | undefined)}
        onChange={(value) => form.setData("title", value)}
        value={form.data.title}
      />
    </EditFrame>
  );
};

ProductEditTitlePage.layout = (page: ReactNode) => <PersistentLayout>{page}</PersistentLayout>;

export default ProductEditTitlePage;
